import { FloatingFormItem, InputDecimal } from '@/components/_common';
import CouponPaymentMethodSelect from '@/components/_common/CouponPaymentMethodSelect';
import { useTaxCouponPaymentEdit } from '@/core/domain/coupon/coupons/coupons.hooks';
import {
  TaxCouponPayment,
  TaxCouponPaymentType,
} from '@/core/domain/coupon/coupons/coupons.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import {
  Row,
  Col,
  Input,
  Form,
  Modal,
  ModalProps,
  Statistic,
  Typography,
  Divider,
} from 'antd';
import { useEffect } from 'react';
import { useInvoiceCFEFormContext } from '../../InvoiceCFEFormProvider';
import formatterPrice from '@/utils/number/formatterPrice';
import CardAccreditorSelect from '@/components/_common/CardAccreditorSelect';

interface Props extends ModalProps {
  payment?: TaxCouponPayment;
  onCancel?: () => void;
}

const InvoiceCFePaymentEditModal: React.FC<Props> = ({ payment, ...props }) => {
  const ruleRequired = useDefaultEmptyFieldError();

  const { taxCoupon } = useInvoiceCFEFormContext();

  const [form] = Form.useForm<TaxCouponPayment>();
  const formValues = Form.useWatch([], form);

  const paymentEdit = useTaxCouponPaymentEdit();

  useEffect(() => {
    if (payment && props.open) {
      form.setFieldsValue({ ...payment });
    }
  }, [payment, props.open]);

  const hasCard =
    formValues?.paymentType === TaxCouponPaymentType.DEBIT_CARD ||
    formValues?.paymentType === TaxCouponPaymentType.CREDIT_CARD;

  const expensesValues = taxCoupon?.couponItems?.reduce(
    (acc, item) => acc + (item.expenses || 0),
    0,
  );

  const discountValues = taxCoupon?.couponItems?.reduce(
    (acc, item) => acc + (item.discount || 0),
    0,
  );

  const unitaryValues = taxCoupon?.couponItems?.reduce(
    (acc, item) => acc + item.unitValue * item.quantity,
    0,
  );

  const amountValues = taxCoupon?.couponItems?.reduce(
    (acc, item) =>
      acc +
      (item.unitValue * item.quantity -
        (item.expenses || 0) -
        (item.discount || 0)),
    0,
  );

  function handleClose() {
    form.resetFields();
    !!props.onCancel && props.onCancel();
  }

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      if (taxCoupon) {
        await paymentEdit.mutateAsync({ ...values, taxCouponId: taxCoupon.id });
        handleClose();
      }
    } catch {}
  }

  return (
    <>
      <Modal
        {...props}
        title="Editar pagamento"
        onCancel={handleClose}
        onOk={handleSubmit}
        destroyOnClose
        confirmLoading={paymentEdit.isLoading}
        okText="Salvar"
        width={600}
      >
        <Typography.Title level={5}>Valores dos itens</Typography.Title>

        <Row gutter={[16, 16]}>
          <Col span={24} sm={12} md={8} lg={6}>
            <Statistic
              value={formatterPrice(unitaryValues || 0)}
              title="Preço X Qtde."
            />
          </Col>

          <Col span={24} sm={12} md={8} lg={6}>
            <Statistic
              value={formatterPrice(discountValues || 0)}
              title="Desconto"
            />
          </Col>

          <Col span={24} sm={12} md={8} lg={6}>
            <Statistic
              value={formatterPrice(expensesValues || 0)}
              title="Despesas"
            />
          </Col>

          <Col span={24} sm={12} md={8} lg={6}>
            <Statistic
              value={formatterPrice(amountValues || 0)}
              title="Valor total"
            />
          </Col>
        </Row>

        <Divider />

        <Form form={form}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col span={24} md={12}>
              <FloatingFormItem
                name="paymentType"
                label="Tipo de pagamento"
                rules={[ruleRequired]}
              >
                <CouponPaymentMethodSelect />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12}>
              <FloatingFormItem
                name="value"
                label="Valor a pagar (R$)"
                rules={[ruleRequired]}
              >
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            {hasCard && (
              <>
                <Col span={24} md={12}>
                  <FloatingFormItem
                    name={['acquirer', 'type']}
                    label="Credenciadora do cartão"
                    rules={[ruleRequired]}
                  >
                    <CardAccreditorSelect />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12}>
                  <FloatingFormItem
                    name={['acquirer', 'authorizationCode']}
                    label="Cód. de autorização"
                    rules={[ruleRequired]}
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>
              </>
            )}
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default InvoiceCFePaymentEditModal;
